import { useState, useContext, useEffect } from 'react';
import { X, UserPlus, Search, User } from 'lucide-react';
import AuthContext from '../../../../../../../context/AuthContext';
import { useToast } from '../../../../../../../context/ToastContext';
import auditProgramService from '../../../../../../../services/audit_services/auditProgramService';
import auditUserManagementService from '../../../../../../../services/audit_services/auditUserManagementService';

export default function AddPartyModal({ isOpen, onClose, programId, onSuccess }) {
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('participant');

  const roleOptions = [
    { value: 'auditor', label: 'Auditeur' },
    { value: 'auditee', label: 'Audité' },
    { value: 'participant', label: 'Participant' },
    { value: 'approver', label: 'Approbateur' }
  ];

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen, searchTerm]);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const result = await auditUserManagementService.filterAuditUserSearch(
        authTokens.access,
        searchTerm
      );
      
      if (result.success) {
        setUsers(result.data || []);
      } else {
        showError(result.error || 'Error loading users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showError('Error loading users');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAddParty = async () => {
    if (!selectedUser) {
      showError('Veuillez sélectionner un utilisateur');
      return;
    }

    setIsLoading(true);
    
    try {
      const result = await auditProgramService.addPartyToAuditProgram(
        authTokens.access,
        programId,
        selectedUser.id,
        selectedRole
      );
      
      if (result.success) {
        showSuccess(result.data.detail || 'Partie ajoutée avec succès');
        onSuccess();
        handleClose();
      } else {
        showError(result.error || 'Échec de l\'ajout de la partie');
      }
    } catch (error) {
      console.error('Error adding party:', error);
      showError('Une erreur s\'est produite lors de l\'ajout de la partie');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedUser(null);
    setSelectedRole('participant');
    setUsers([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <UserPlus className="h-5 w-5 mr-2 text-teal-600" />
            Ajouter une partie au programme
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isLoading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* User Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rechercher et sélectionner un utilisateur
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher des utilisateurs par nom ou email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>

          {/* Users List */}
          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
            {usersLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                <span className="ml-2 text-sm text-gray-600">Chargement des utilisateurs...</span>
              </div>
            ) : users.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {users.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => setSelectedUser(user)}
                    className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedUser?.id === user.id ? 'bg-teal-50 border-l-4 border-teal-500' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <User className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">Aucun utilisateur trouvé</p>
              </div>
            )}
          </div>

          {/* Selected User Display */}
          {selectedUser && (
            <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
              <p className="text-sm font-medium text-teal-900">Utilisateur sélectionné:</p>
              <p className="text-sm text-teal-800">
                {selectedUser.first_name} {selectedUser.last_name} ({selectedUser.email})
              </p>
            </div>
          )}

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rôle de la partie
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuler
          </button>
          <button
            onClick={handleAddParty}
            disabled={isLoading || !selectedUser}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Ajout en cours...
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-2" />
                Ajouter une partie
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}